import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { format } from 'date-fns';

const fmtDate = d => d ? format(new Date(d), 'dd/MM/yyyy') : '—';
const fmtAmt = n => n ? `$${parseFloat(n).toLocaleString('en-IN', {minimumFractionDigits:2})}` : '—';

// Small inline icons keep the dashboard dependency-free.
const Icon = ({ name }) => {
  const paths = {
    orders: <><path d="M9 3h6a2 2 0 0 1 2 2v0h1a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1a2 2 0 0 1 2-2Z"/><path d="M9 3a2 2 0 0 0 0 4h6a2 2 0 0 0 0-4"/><path d="M9 12h6M9 16h4"/></>,
    cash: <><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2.5"/><path d="M6 12h.01M18 12h.01"/></>,
    alert: <><path d="M10.3 3.3 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.3a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/></>,
    doc: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6M9 13h6M9 17h6"/></>,
    ship: <><path d="M12 2v6M5 8h14l-2 6H7L5 8Z"/><path d="M4 14c1.5 1 3 1 4.5 0 1.5 1 3 1 4.5 0 1.5 1 3 1 4.5 0"/><path d="M6 20c1.5 1 3 1 4.5 0 1.5 1 3 1 4.5 0"/></>,
    anchor: <><circle cx="12" cy="5" r="2.5"/><path d="M12 8v13M5 12a7 7 0 0 0 14 0M4 12h2M18 12h2"/></>,
    chat: <><path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2Z"/></>,
    check: <><circle cx="12" cy="12" r="9"/><path d="m8.5 12 2.5 2.5 4.5-5"/></>,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {paths[name] || null}
    </svg>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/dashboard').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading dashboard...</div>;
  if (!data) return <div className="alert alert-danger">Failed to load dashboard</div>;

  const { summary, detention_alerts, payment_due_list } = data;

  const statusBadge = s => {
    const map = { 'Pending':'badge-red','Partial':'badge-yellow','Paid':'badge-green' };
    return <span className={`badge ${map[s]||'badge-gray'}`}>{s}</span>;
  };

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  })();

  const cards = [
    { label:'Orders', value: summary.total_orders, icon:'orders', tone:'info', hint:'All orders', path:'/orders' },
    { label:'Payments Due Today', value: summary.payments_today, icon:'cash', tone: summary.payments_today>0?'warning':'success', hint:'Due today', path:'/orders?stage=payments_today' },
    { label:'Overdue Payments', value: summary.overdue_payments, icon:'alert', tone: summary.overdue_payments>0?'danger':'success', hint:'Past due date', path:'/orders?stage=overdue_payments' },
    { label:'Awaiting BL', value: summary.awaiting_bl, icon:'doc', tone:'info', hint:'No BL yet', path:'/orders?stage=awaiting_bl' },
    { label:'Under Sea / Transit', value: summary.under_sea, icon:'ship', tone:'info', hint:'On the water', path:'/orders?stage=under_sea' },
    { label:'At Port / Customs', value: summary.at_port, icon:'anchor', tone:'warning', hint:'Clearing customs', path:'/orders?stage=at_port' },
    { label:'Open Complaints', value: summary.open_complaints, icon:'chat', tone: summary.open_complaints>0?'danger':'success', hint:'Needs attention', path:'/complaints' },
    { label:'Docs Pending Courier', value: summary.pending_docs, icon:'doc', tone: summary.pending_docs>0?'warning':'success', hint:'Not couriered', path:'/orders?stage=pending_docs' },
  ];

  return (
    <div>
      <div className="dash-hero">
        <div>
          <h2>{greeting}! 👋</h2>
          <p>Real-time overview of all import operations</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/orders/new')}>+ New Order</button>
      </div>

      {/* Summary Cards */}
      <div className="grid-stats">
        {cards.map(c => (
          <div key={c.label} className={`stat-card accent-${c.tone}`} onClick={() => navigate(c.path)} title="Click to view">
            <div className="stat-top">
              <div className="label">{c.label}</div>
              <div className={`stat-icon ${c.tone}`}><Icon name={c.icon} /></div>
            </div>
            <div className={`value ${c.tone}`} style={{color: c.tone==='danger'?'var(--accent)':c.tone==='success'?'var(--success)':c.tone==='warning'?'var(--warning)':'var(--info)'}}>{c.value}</div>
            <div className="stat-hint">{c.hint}</div>
          </div>
        ))}
      </div>

      <div className="grid-2" style={{gap:'20px'}}>
        {/* Payments Due */}
        <div className="card">
          <div className="card-head">
            <div className="section-title">Payments Due / Overdue</div>
            <span className={`count-pill ${payment_due_list.length ? 'alert' : ''}`}>{payment_due_list.length}</span>
          </div>
          {payment_due_list.length === 0
            ? <div className="empty-state tidy" style={{color:'var(--success)'}}><Icon name="check" />No pending payments</div>
            : <div className="table-wrap">
                <table>
                  <thead><tr><th>Order</th><th>BL No</th><th>Shipper</th><th>Due Date</th><th>Amount</th><th>Status</th></tr></thead>
                  <tbody>
                    {payment_due_list.map(p => (
                      <tr key={p.id} style={{cursor:'pointer'}} onClick={() => navigate(`/orders/${p.id}`)}>
                        <td className="fw-bold">{p.order_no}</td>
                        <td>{p.bl_no}</td>
                        <td>{p.shipper_name}</td>
                        <td style={{color: new Date(p.due_date) < new Date() ? 'var(--accent)' : 'inherit'}}>{fmtDate(p.due_date)}</td>
                        <td>{fmtAmt(p.actual_amount_due)}</td>
                        <td>{statusBadge(p.payment_status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          }
        </div>

        {/* Detention Alerts */}
        <div className="card">
          <div className="card-head">
            <div className="section-title" style={{color:'var(--accent)'}}>Detention Expiry Alerts</div>
            <span className={`count-pill ${detention_alerts.length ? 'alert' : ''}`}>{detention_alerts.length}</span>
          </div>
          {detention_alerts.length === 0
            ? <div className="empty-state tidy" style={{color:'var(--success)'}}><Icon name="check" />No detention alerts</div>
            : <div className="table-wrap">
                <table>
                  <thead><tr><th>Order</th><th>BL No</th><th>Shipper</th><th>Free Time Upto</th><th>Days</th></tr></thead>
                  <tbody>
                    {detention_alerts.map(d => (
                      <tr key={d.id} style={{cursor:'pointer'}} onClick={() => navigate(`/orders/${d.id}`)}>
                        <td className="fw-bold">{d.order_no}</td>
                        <td>{d.bl_no}</td>
                        <td>{d.shipper_name}</td>
                        <td>{fmtDate(d.free_time_upto)}</td>
                        <td><span className="detention-badge">{d.days_overdue > 0 ? `+${d.days_overdue} overdue` : 'Expiring soon'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          }
        </div>
      </div>
    </div>
  );
}
